import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { AwardApiService } from '../../../core/api-services/award-api.service';
import { ExpenseApiService } from '../../../core/api-services/expense-api.service';
import { ScholarshipApiService } from '../../../core/api-services/scholarship-api.service';
import { ScholarshipApplicationApiService } from '../../../core/api-services/scholarship-application-api.service';
import { AdminApplication } from '../../../core/models/admin-application';
import { AuthorizationService } from '../../../core/authorization/authorization.service';
import { Permissions } from '../../../core/authorization/permissions';
import { AllocationPaymentReport, ScholarshipClaimReport } from '../../../core/models/expense';
import { AwardStatus, GrantCycleAwardsResult, ScholarshipAwardSummary } from '../../../core/models/scholarship-award';
import { Scholarship } from '../../../core/models/scholarship';
import { PaginationComponent } from '../../../shared/pagination/pagination';

/**
 * Administration page for a single scholarship.
 *
 * Shows what the scholarship is worth and how much of it is committed, which
 * students hold an award for the cycle, and every application that was ever
 * submitted for it. The administrator can award the cycle from here, scoped to
 * this scholarship only.
 *
 * Reached from Scholarship Management ("View"); the scholarship id arrives as an
 * input from the admin shell, which reads it from the query string.
 */
@Component({
  selector: 'app-admin-scholarship-cycle',
  standalone: true,
  imports: [CommonModule, DatePipe, FormsModule, PaginationComponent],
  templateUrl: './scholarship-cycle.html',
  styleUrl: './scholarship-cycle.scss',
})
export class AdminScholarshipCycleComponent implements OnInit {
  private readonly scholarshipApi = inject(ScholarshipApiService);
  private readonly applicationApi = inject(ScholarshipApplicationApiService);
  private readonly awardApi = inject(AwardApiService);
  private readonly expenseApi = inject(ExpenseApiService);

  readonly authorization = inject(AuthorizationService);
  readonly permissions = Permissions;

  /** The scholarship to administer. Zero means "nothing selected". */
  @Input() scholarshipId = 0;

  /** Cycle to report on. Defaults to the current year. */
  @Input() cycle = '';

  /** Raised when the administrator wants to go back to Scholarship Management. */
  @Output() readonly back = new EventEmitter<void>();

  readonly scholarship = signal<Scholarship | null>(null);
  readonly allocationReport = signal<AllocationPaymentReport | null>(null);
  readonly claimReport = signal<ScholarshipClaimReport | null>(null);
  readonly awards = signal<ScholarshipAwardSummary[]>([]);
  readonly applications = signal<AdminApplication[]>([]);

  readonly loading = signal(false);
  readonly error = signal('');
  readonly message = signal('');
  readonly busy = signal(false);
  readonly cycleResult = signal<GrantCycleAwardsResult | null>(null);

  /** Applications panel filters, applied in the browser. */
  statusFilter = 'ALL';
  searchTerm = '';

  readonly pageSizeOptions = [10, 25, 50, 100];
  pageSize = 10;
  currentPage = 1;

  /** Cycle name used by the "award this cycle" run; starts as the shown cycle. */
  grantCycleName = '';

  /** Dates for the "award this cycle" run. */
  startDate = '';
  endDate = '';

  ngOnInit(): void {
    if (!this.cycle) {
      this.cycle = `${new Date().getFullYear()}`;
    }

    this.grantCycleName = this.cycle;
    this.startDate = `${this.cycle}-01-01`;
    this.endDate = `${this.cycle}-12-31`;

    this.load();
  }

  // ---- Loading -----------------------------------------------------------

  /** Loads every panel on the page. */
  load(): void {
    if (!this.scholarshipId) {
      this.error.set('No scholarship was selected.');
      return;
    }

    this.loading.set(true);
    this.error.set('');
    this.cycleResult.set(null);

    this.loadScholarship();
    this.loadAwards();
    this.loadBudget();

    this.applicationApi
      .getApplicationsForScholarship(this.scholarshipId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (applications) => {
          this.applications.set(applications ?? []);
          this.currentPage = 1;
        },
        error: () => {
          this.applications.set([]);
          this.error.set('The applications for this scholarship could not be loaded.');
        },
      });
  }

  private loadScholarship(): void {
    this.scholarshipApi.getScholarshipById(this.scholarshipId).subscribe({
      next: (scholarship) => this.scholarship.set(scholarship),
      error: () => this.scholarship.set(null),
    });
  }

  private loadAwards(): void {
    this.awardApi.getAwardsForReview(this.cycle, this.scholarshipId).subscribe({
      next: (awards) => this.awards.set(awards ?? []),
      error: () => this.awards.set([]),
    });
  }

  /** Allocation and claim totals for the scholarship in the selected cycle. */
  private loadBudget(): void {
    this.expenseApi.getAllocationReport(this.cycle, this.scholarshipId).subscribe({
      next: (reports) => this.allocationReport.set(reports?.[0] ?? null),
      error: () => this.allocationReport.set(null),
    });

    this.expenseApi.getClaimReport(this.cycle, this.scholarshipId).subscribe({
      next: (reports) => this.claimReport.set(reports?.[0] ?? null),
      error: () => this.claimReport.set(null),
    });
  }

  /** Re-reads the cycle after the cycle filter changes. */
  applyCycle(): void {
    this.grantCycleName = this.cycle;
    this.startDate = `${this.cycle}-01-01`;
    this.endDate = `${this.cycle}-12-31`;
    this.load();
  }

  // ---- Budget figures ----------------------------------------------------

  get currency(): string {
    return this.allocationReport()?.currency ?? this.claimReport()?.currency ?? '';
  }

  get totalAllocated(): number {
    return this.allocationReport()?.totalAllocated ?? 0;
  }

  get totalUsed(): number {
    return this.allocationReport()?.totalUsed ?? 0;
  }

  get totalRemaining(): number {
    return this.allocationReport()?.totalRemaining ?? 0;
  }

  get totalClaims(): number {
    return this.claimReport()?.totalClaims ?? 0;
  }

  get awardCount(): number {
    return this.allocationReport()?.awardCount ?? this.awards().length;
  }

  get activeAwardCount(): number {
    return this.allocationReport()?.activeAwardCount ?? this.activeAwards.length;
  }

  /** Funds the scholarship record itself advertises, for comparison. */
  get allocatedFunds(): number {
    return this.scholarship()?.allocatedFunds ?? 0;
  }

  /** Percentage of the awarded allocation that has been used. */
  get usagePercent(): number {
    if (this.totalAllocated <= 0) {
      return 0;
    }

    return Math.round((this.totalUsed / this.totalAllocated) * 100);
  }

  get usageWidth(): string {
    return `${Math.min(100, Math.max(0, this.usagePercent))}%`;
  }

  // ---- Cycle status ------------------------------------------------------

  /** Whether the scholarship is still accepting applications. */
  get cycleOpen(): boolean {
    const deadline = this.scholarship()?.deadline;
    return !!deadline && new Date(deadline).getTime() > Date.now();
  }

  get cycleStatusLabel(): string {
    return this.cycleOpen ? 'Open for applications' : 'Applications closed';
  }

  /** Whole days left before the deadline, negative once it has passed. */
  get daysToDeadline(): number | null {
    const deadline = this.scholarship()?.deadline;
    if (!deadline) {
      return null;
    }

    return Math.ceil((new Date(deadline).getTime() - Date.now()) / 86_400_000);
  }

  /** Positive number of days a passed deadline is overdue by. */
  get daysOverdue(): number {
    const days = this.daysToDeadline;
    return days === null ? 0 : Math.max(0, -days);
  }

  // ---- Awarded students --------------------------------------------------

  get activeAwards(): ScholarshipAwardSummary[] {
    return this.awards().filter((award) => award.status === 'ACTIVE');
  }

  // ---- Applications ------------------------------------------------------

  /** Distinct statuses present on this scholarship's applications. */
  get applicationStatuses(): string[] {
    return [
      ...new Set(
        this.applications()
          .map((application) => application.applicationStatus?.name)
          .filter((name): name is string => !!name)
      ),
    ].sort();
  }

  /** Applications matching the status and free-text filters. */
  get filteredApplications(): AdminApplication[] {
    const term = this.searchTerm.trim().toLowerCase();

    return this.applications().filter((application) => {
      if (this.statusFilter !== 'ALL' && application.applicationStatus?.name !== this.statusFilter) {
        return false;
      }

      if (!term) {
        return true;
      }

      const haystack = [
        application.referenceNumber,
        this.applicantName(application),
        application.user?.emailAddress,
        application.user?.identityNumber,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });
  }

  get paginatedApplications(): AdminApplication[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredApplications.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredApplications.length / this.pageSize));
  }

  get showingFrom(): number {
    return this.filteredApplications.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingTo(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredApplications.length);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }

    this.currentPage = page;
  }

  /** Any filter change restarts the list at page one. */
  onFilterChange(): void {
    this.currentPage = 1;
  }

  // ---- Actions -----------------------------------------------------------

  /** Awards every approved application for the cycle. A dry run previews first. */
  grantCycle(dryRun: boolean): void {
    if (this.busy()) {
      return;
    }

    this.busy.set(true);
    this.error.set('');
    this.message.set('');
    this.cycleResult.set(null);

    this.awardApi
      .grantCycleAwards({
        cycle: this.grantCycleName,
        startDate: this.startDate,
        endDate: this.endDate,
        scholarshipId: this.scholarshipId,
        dryRun,
      })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (result) => {
          this.cycleResult.set(result);

          if (!result.dryRun) {
            this.message.set(
              `Awarded ${result.awarded} application${result.awarded === 1 ? '' : 's'} for cycle ${result.cycle}.`
            );
            this.loadAwards();
            this.loadBudget();
          }
        },
        error: () => this.error.set('The cycle could not be awarded. Please try again.'),
      });
  }

  requestBack(): void {
    this.back.emit();
  }

  // ---- Presentation helpers ---------------------------------------------

  applicantName(application: AdminApplication): string {
    return [application.user?.firstName, application.user?.lastName].filter(Boolean).join(' ') || 'Unknown applicant';
  }

  /** Maps a claim or application status onto the shared `.status` pill classes. */
  statusClass(statusName: string | null | undefined): string {
    switch ((statusName ?? '').toUpperCase()) {
      case 'APPROVED':
      case 'PAID':
        return 'status status-approved';
      case 'DECLINED':
      case 'REJECTED':
        return 'status status-rejected';
      case 'CANCELLED':
        return 'status status-cancelled';
      default:
        return 'status status-pending';
    }
  }

  /** Maps an award lifecycle status onto the shared `.status` pill classes. */
  awardStatusClass(status: AwardStatus): string {
    switch (status) {
      case 'ACTIVE':
        return 'status status-approved';
      case 'ON_HOLD':
        return 'status status-pending';
      case 'TERMINATED':
        return 'status status-rejected';
      default:
        return 'status status-cancelled';
    }
  }

  money(value: number | null | undefined, currency: string | null | undefined): string {
    return `${(value ?? 0).toFixed(2)} ${currency ?? ''}`.trim();
  }
}
